import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/diensten', label: t('nav.services') },
    { to: '/gemeentediensten', label: t('nav.municipal') },
    { to: '/projecten', label: t('nav.projects') },
    { to: '/galerij', label: t('nav.gallery') },
    { to: '/over-ons', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav data-testid="navbar" className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
      <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#FF5F00] rounded-lg flex-shrink-0" aria-hidden="true" />
          <div className="hidden sm:flex flex-col">
            <span className="font-extrabold text-[#0F172A] text-sm leading-tight">
              <span className="text-[#FF5F00]">Orange</span> Onderhoud
            </span>
            <span className="text-[10px] text-[#64748B] font-medium tracking-wide uppercase">bedrijf</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.to.replace('/', '').replace('-', '') || 'home'}`}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive(link.to)
                  ? 'text-[#FF5F00] bg-orange-50'
                  : 'text-[#334155] hover:text-[#FF5F00] hover:bg-slate-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right: Lang switcher + CTA */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1" data-testid="language-switcher">
            {['nl', 'en', 'es'].map(l => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}`}
                className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all ${
                  lang === l ? 'bg-[#FF5F00] text-white shadow-sm' : 'text-[#64748B] hover:text-[#334155]'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <Link
            to="/contact"
            data-testid="navbar-cta"
            className="hidden lg:flex items-center bg-[#FF5F00] text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-[#EA580C] transition-all shadow-sm shadow-orange-200 active:scale-95"
          >
            {t('nav.quote')}
          </Link>
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[#334155] rounded-lg hover:bg-slate-100 transition-colors"
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-100 px-6 py-5 flex flex-col gap-1 shadow-lg">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                isActive(link.to)
                  ? 'text-[#FF5F00] bg-orange-50'
                  : 'text-[#334155] hover:text-[#FF5F00] hover:bg-slate-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="mt-2 bg-[#FF5F00] text-white px-4 py-3 rounded-lg text-sm font-bold text-center hover:bg-[#EA580C] transition-all"
          >
            {t('nav.quote')}
          </Link>
        </div>
      )}
    </nav>
  );
}
